<template>
  <BaseEditModal
    :displayName="queueMaster.name"
    helpFileName="EditQueueModal"
    :itemtype="'scene'"
    :tabs="tabs"
    :icon="'mdi-playlist-play'"
    :masterObj="queueMaster"
    :saveFunc="saveQueue"
    :deleteFunc="deleteQueue"
    :instanceDeletable="true"
    :startTab="go_to_tab"
    @close="emit('close')"
    @locked="(bool) => emit('locked', bool)">
    <template v-slot:prepend-buttons>
      <v-btn
        class="mr-2"
        style="background-color: var(--inner-background)"
        variant="flat"
        @click="cloneQueue()">
        <v-icon style="font-size: 22px">mdi-content-duplicate</v-icon>
      </v-btn>
      <v-btn
        style="background-color: var(--inner-background)"
        variant="flat"
        :loading="isExporting"
        :disabled="isExporting"
        @click="showExportDialog = true">
        <template v-if="!isExporting">
          <v-icon style="font-size: 22px">mdi-download</v-icon>
        </template>
        <template v-else>
          <v-progress-circular
            indeterminate
            size="24"
            color="primary" />
        </template>
      </v-btn>
    </template>
  </BaseEditModal>

  <!-- Export Report Modal -->
  <ExportReportModal
    v-model="showExportReport"
    :report="exportReport"
    :fileName="exportFileName"
    :estimatedSize="exportEstimatedSize"
    @close="showExportReport = false" />

  <!-- Export Options Dialog -->
  <v-dialog v-model="showExportDialog" max-width="400px">
    <v-card>
      <v-card-title>Export Queue</v-card-title>
      <v-card-text>
        <v-btn
          block
          class="mb-2"
          variant="outlined"
          prepend-icon="mdi-code-json"
          :loading="isExporting"
          @click="showExportDialog = false; exportQueue()">
          Export data only (.cprq)
        </v-btn>
        <v-btn
          block
          variant="outlined"
          prepend-icon="mdi-package-variant-closed"
          :loading="isExporting"
          @click="showExportDialog = false; exportQueueArchive()">
          Include files (.cprqa)
        </v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script setup>
import { deluge, state } from '../../stores/deluge';
import BaseEditModal from '../Shared/Modals/BaseEditModal.vue';
import ExportReportModal from '../Shared/Modals/ExportReportModal.vue';
import { $http } from '../../stores/http';

import {
  defineProps,
  defineEmits,
  ref,
  readonly,
  onMounted,
  reactive,
} from 'vue';

// Template Sub Components
import QueueDetails from './QueueModalComponents/QueueDetails.vue';
import ScenePlayConditions from './SceneModalComponents/ScenePlayConditions.vue';
import SceneTriggers from './SceneModalComponents/SceneTriggers.vue';
import QueueIngest from './QueueModalComponents/QueueIngest.vue';
// template_index is actually a UID
const props = defineProps(['current_queue', 'current_channel', 'go_to_tab']);
const emit = defineEmits(['close', 'locked']);

const queueMaster = JSON.parse(JSON.stringify(props.current_queue));
const showExportDialog = ref(false);
const isExporting = ref(false);
const showExportReport = ref(false);
const exportReport = ref(null);
const exportFileName = ref('');
const exportEstimatedSize = ref(0);

async function cloneQueue() {
  try {
    const response = await $http(
      'POST',
      '/dynamic_engine/queue_from_queue?queue_uid=' +
        props.current_queue.uid +
        '&channel_did=' +
        props.current_channel
    );
  } catch (error) {
    console.log(error);
  }
  emit('close');
}

async function exportQueue() {
  try {
    isExporting.value = true;

    const response = await $http(
      'GET',
      '/dynamic_engine/export_queue?queue_uid=' + props.current_queue.uid
    );

    // Parse the response to get the export data and report
    const data = typeof response === 'string' ? JSON.parse(response) : response;

    // Extract the export report from meta if available
    if (data.meta?.export_report) {
      exportReport.value = data.meta.export_report;
    }

    // Download as .cprq
    const fileName = queueMaster.name + '.cprq';
    exportFileName.value = fileName;

    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' +
        encodeURIComponent(
          typeof response === 'string' ? response : JSON.stringify(data)
        )
    );
    element.setAttribute('download', fileName);

    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    // Show the report modal
    showExportReport.value = true;
  } catch (error) {
    console.error('Export failed:', error);
  } finally {
    isExporting.value = false;
  }
}

async function exportQueueArchive() {
  const queueUid = props.current_queue.uid;
  const queueName = queueMaster.name;
  isExporting.value = true;

  try {
    // 1. Get archive info (estimated size, file counts)
    const infoResponse = await $http(
      'GET',
      '/dynamic_engine/export_queue_archive_info?queue_uid=' + queueUid
    );
    const info =
      typeof infoResponse === 'string' ? JSON.parse(infoResponse) : infoResponse;

    exportReport.value = {
      statistics: {},
      files: {
        template_files: info.template_files,
        asset_files: info.asset_files,
        thumbnail_files: info.thumbnail_files ?? 0,
        total_files: info.total_files,
      },
      warnings: [],
      warning_count: 0,
      success: true,
    };
    exportFileName.value = queueName + '.cprqa';
    exportEstimatedSize.value = info.estimated_size ?? 0;

    // 2. Trigger native browser download (streams directly to disk).
    // The browser's download UI shows progress.
    const token = localStorage.getItem('authentication_token') ?? '';
    const downloadUrl =
      state.devRedirect +
      'api/dynamic_engine/export_queue_archive?queue_uid=' +
      queueUid +
      '&token=' +
      encodeURIComponent(token);

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 5000);

    showExportReport.value = true;
  } catch (error) {
    console.error('Archive export failed:', error);
  } finally {
    isExporting.value = false;
  }
}

// Tabs - must be 0 indexed.
const tabs = [
  {
    id: 0,
    title: 'Details',
    icon: 'mdi-rename-box',
    display: true,
    component: QueueDetails,
  },
  {
    id: 1,
    title: 'Conditions',
    icon: 'mdi-motion-play-outline',
    display: true,
    component: ScenePlayConditions,
    props: {
      itemtype: 'queue',
    },
  },
  {
    id: 2,
    title: 'Triggers',
    icon: 'mdi-share-all',
    display: true,
    component: SceneTriggers,
    props: {
      current_channel: props.current_channel,
      itemtype: 'queue',
    },
  },
  {
    id: 3,
    title: 'Ingest Tags',
    icon: 'mdi-filter-plus',
    display: true,
    component: QueueIngest,
  },
];

async function saveQueue(sceneUID, newTemplateData) {
  let toSend = JSON.parse(JSON.stringify(newTemplateData));
  return await $http(
    'PUT',
    '/dynamic_engine/update_queue',
    JSON.stringify(toSend)
  );
}

async function deleteQueue() {
  // Get references BEFORE any async operations or state changes
  const deletedQueueId = props.current_queue.id;
  const deletedQueueUid = props.current_queue.uid;
  const deletedQueueDid = props.current_queue.did;
  const deletedQueueItem = deluge.items[deletedQueueUid];
  if (!deletedQueueItem) {
    console.warn(
      '[deleteQueue] Queue item not found in deluge.items for uid:',
      deletedQueueUid
    );
  }

  // Clear the ConditionPicker's polling state to prevent 404 errors after deletion
  // This ensures the interval stops trying to fetch data for the deleted queue
  if (
    state.sceneConditionSummaryModal === deletedQueueUid ||
    state.sceneConditionSummaryModal === deletedQueueDid
  ) {
    state.sceneConditionSummaryModal = null;
  }
  if (
    state.sceneConditionSummary?.did === deletedQueueUid ||
    state.sceneConditionSummary?.uid === deletedQueueUid ||
    state.sceneConditionSummary?.did === deletedQueueDid
  ) {
    state.sceneConditionSummary = null;
  }
  // Also clear the interval task if it exists
  if (state.sceneConditionSummaryTask) {
    clearInterval(state.sceneConditionSummaryTask);
    state.sceneConditionSummaryTask = null;
  }

  // Clean up the deleted queue's own navigation state (if any)
  if (state.nested_queues[deletedQueueId]) {
    delete state.nested_queues[deletedQueueId];
  }

  // If this queue has a parent (it's a nested queue), we need to navigate back
  // by popping the navigation stack of the top-level parent queue
  if (deletedQueueItem?.pdid) {
    // Get the immediate parent
    const parentDid = deletedQueueItem.pdid;
    const parentItem = deluge.items[parentDid];

    if (parentItem) {
      // Find the top-level (root) queue by traversing up the hierarchy
      // The top-level queue is the one whose parent is a channel (item_type !== 2)
      let rootQueue = parentItem;
      while (rootQueue && deluge.items[rootQueue.pdid]?.item_type === 2) {
        rootQueue = deluge.items[rootQueue.pdid];
      }

      // Pop the navigation stack to go back one level
      // The root queue's ID is what's used as the key in state.nested_queues
      if (rootQueue && state.nested_queues[rootQueue.id]) {
        state.nested_queues[rootQueue.id].pop();
      }
    }
  }

  console.log(
    '[deleteQueue] channels.queues:',
    deluge.channels[props.current_channel]?.queues
  );
  console.log(
    '[deleteQueue] nested_queues:',
    JSON.stringify(state.nested_queues)
  );

  try {
    await $http(
      'DELETE',
      '/dynamic_engine/queue?id=' +
        deletedQueueId +
        '&channel_did=' +
        props.current_channel
    );
    emit('close');
  } catch (error) {
    console.log('[deleteQueue] error:', error);
    emit('close');
  }
}

// END Tabs
</script>

<style scoped></style>
